import React, { useEffect, useState } from "react";
import {
  getAllDiscountCodes,
  createDiscountCode,
  updateDiscountCode,
  deleteDiscountCode,
// } from "../../services/discounts/discountCodesService";
} from "../../services/discounts/adminDiscountsService";


import { useTranslation } from "react-i18next";
import { useLoading } from "../../components/LoadingProvider";
import LanguageDropdown from "../../components/LanguageDropdown";
import LanguageFlag from "../../components/LanguageFlag";
import Util from "../../components/Util";

const DiscountAdmin = () => {
  const initialFormState = () => ({
    code: "",
    description: "",
    percent: "",
    maxUsageCount: 1000,
    maxUsageCountPerUser: 1,
    expiresAt: "",
    language: "fa",
    isActive: true,
  });

  const { t } = useTranslation();
  const { toast } = useLoading();

  const [discountCodes, setDiscountCodes] = useState([]);
  const [formData, setFormData] = useState(initialFormState());
  const [editId, setEditId] = useState(null);

  const fetchDiscountCodes = async () => {
    const res = await getAllDiscountCodes();
    setDiscountCodes(res);
  };

  useEffect(() => {
    fetchDiscountCodes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      percent: +Util.toEnglishDigits(formData.percent),
      maxUsageCount: +Util.toEnglishDigits(formData.maxUsageCount),
      maxUsageCountPerUser: +Util.toEnglishDigits(formData.maxUsageCountPerUser),
      expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null,
    };

    try {
      if (editId) {
        await updateDiscountCode(editId, payload);
        toast.success("Discount code updated successfully");
      } else {
        await createDiscountCode(payload);
        toast.success("Discount code created successfully");
      }
      setFormData(initialFormState());
      setEditId(null);
      fetchDiscountCodes();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (code) => {
    setFormData({
      ...code,
      percent: code.percent.toString(),
      maxUsageCount: code.maxUsageCount?.toString() || "",
      maxUsageCountPerUser: code.maxUsageCountPerUser?.toString() || "",
      expiresAt: code.expiresAt ? code.expiresAt.slice(0, 10) : "",
    });
    setEditId(code._id);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t("confirmDelete"))) {
      await deleteDiscountCode(id);
      fetchDiscountCodes();
    }
  };

  const resetForm = () => {
    setFormData(initialFormState());
    setEditId(null);
  };

  return (
    <div className="container">
      <h2>{t("console.discounts.form.title")}</h2>

      <form onSubmit={handleSubmit} className="row g-3 my-4">
        <div className="col-md-4">
          <label className="form-label">{t("console.discounts.form.code")}</label>
          <input
            type="text"
            className="form-control"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            required
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">{t("console.discounts.form.percent")}</label>
          <input
            type="number"
            className="form-control"
            value={formData.percent}
            onChange={(e) =>
              setFormData({ ...formData, percent: e.target.value })
            }
            required
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">
            {t("console.discounts.form.maxUsageCount")}
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.maxUsageCount}
            onChange={(e) =>
              setFormData({ ...formData, maxUsageCount: e.target.value })
            }
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">
            {t("console.discounts.form.maxUsageCountPerUser")}
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.maxUsageCountPerUser}
            onChange={(e) =>
              setFormData({ ...formData, maxUsageCountPerUser: e.target.value })
            }
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">{t("console.discounts.form.expiresAt")}</label>
          <input
            type="date"
            className="form-control"
            value={formData.expiresAt}
            onChange={(e) =>
              setFormData({ ...formData, expiresAt: e.target.value })
            }
          />
        </div>

        <div className="col-md-4">
          <LanguageDropdown
            defaultValue={formData.language}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, language: value }))
            }
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">{t("console.discounts.form.description")}</label>
          <textarea
            className="form-control"
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </div>

        <div className="col-md-2 form-check mt-4">
          <input
            type="checkbox"
            className="form-check-input"
            checked={formData.isActive}
            onChange={(e) =>
              setFormData({ ...formData, isActive: e.target.checked })
            }
          />
          <label className="form-check-label">
            {t("console.discounts.form.isActive")}
          </label>
        </div>

        <div className="col-12">
          <button type="submit" className="btn btn-primary">
            {editId ? t("console.discounts.actions.update") : t("console.discounts.actions.create")}
          </button>

          {editId && (
            <button
              type="button"
              className="btn btn-secondary ms-2"
              onClick={resetForm}
            >
              {t("console.discounts.actions.cancel")}
            </button>
          )}
        </div>
      </form>

      <hr />

      <table className="table">
        <thead>
          <tr>
            <th>{t("console.discounts.form.code")}</th>
            <th>{t("console.discounts.form.percent")}</th>
            <th>{t("console.discounts.form.maxUsageCount")}</th>
            <th>{t("console.discounts.form.maxUsageCountPerUser")}</th>
            <th>{t("console.discounts.form.expiresAt")}</th>
            <th>{t("console.discounts.form.language")}</th>
            <th>{t("console.discounts.form.isActive")}</th>
            <th>{t("console.discounts.actions.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {discountCodes.map((code) => (
            <tr key={code._id}>
              <td>{code.code}</td>
              <td>{code.percent}%</td>
              <td>{code.maxUsageCount}</td>
              <td>{code.maxUsageCountPerUser}</td>
              <td>{code.expiresAt ? new Date(code.expiresAt).toLocaleString("fa-IR") : "-"}</td>
              <td>
                <span style={{ fontSize: "22px" }}>
                  <LanguageFlag language={code.language} />
                </span>
              </td>
              <td>{code.isActive ? t("console.discounts.yes") : t("console.discounts.no")}</td>
              <td>
                <button
                  className="btn btn-sm btn-warning me-2"
                  onClick={() => handleEdit(code)}
                >
                  {t("console.discounts.actions.edit")}
                </button>
                {/* <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(code._id)}
                >
                  {t("console.discounts.actions.delete")}
                </button> */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DiscountAdmin;


 
